import {useEffect, useRef} from 'react';
import {useMap} from 'react-leaflet';
import L from 'leaflet';
import {CIBLE_REMPLISSAGE, MARQUEUR, NAVY, VERDICT_COULEURS} from '../../tokens.js';
import {cellulesGrille, clusteriser, contourAbribac, couleurPart, couleurRemplissage, lisereAbribac, poidsDefaut, rgbRemplissage, tailleAbribac, PALIERS_REMPLISSAGE} from './carte.selectors.js';
import {moyenneLocale, poidsRemplissage} from './carte.heatmap.js';
import {ABRIBAC, GLYPHES, LARGEUR_GLYPHE} from '../AbribacIcon.js';

/*
 * Couche canvas des PAV (PIL-01 lot 4), dupliquée de `patrimoineV2/ConteneurCanvasLayer` : même principe (renderer
 * Canvas natif Leaflet, dessin dans son contexte, hit-test à la main) mais sans Redux (`onSelect(pavId)` à la place du
 * dispatch), avec les marqueurs du handoff (rond 26 px, liseré blanc, icône blanche) et les cinq styles de la carte.
 * Des sprites par verdict sont dessinés une fois : 2 800 marqueurs = 2 800 drawImage.
 */

const NI = VERDICT_COULEURS.ni.marqueur;
const RAYON_CLIC = 13; // px : rayon du marqueur
const RAYON_CLIC_SELECTION = 16; // px : le marqueur sélectionné (32 px) est plus gros
const RAYON_HEAT = 26;
const FLOU_HEAT = 18;

// Icônes du handoff (viewBox 24) : ✓ / clé / × / ? (non inspecté).
const ICONES = {
    ras: {trace: new Path2D('m6 12.5 4 4 8-9'), mode: 'stroke', largeur: 2.6},
    pb: {trace: new Path2D('M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z'), mode: 'fill'},
    imp: {trace: new Path2D('m7 7 10 10M17 7 7 17'), mode: 'stroke', largeur: 2.8},
    ni: {trace: new Path2D('M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.3-1 .9-1 1.7M12 17h.01'), mode: 'stroke', largeur: 2.6, couleur: '#4A4A4A'},
};

const sprites = new Map();

/** Sprite d'un marqueur : verdict x (normal | sélectionné), au ratio d'écran courant. */
function sprite(verdict, selectionne) {
    const dpr = window.devicePixelRatio || 1;
    const cle = `${verdict}|${selectionne ? 1 : 0}|${dpr}`;
    let s = sprites.get(cle);
    if (s) return s;
    const taille = selectionne ? MARQUEUR.tailleSelection : MARQUEUR.taille;
    const marge = selectionne ? 8 : 5; // halo + ombre
    const cote = taille + marge * 2;
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(cote * dpr);
    canvas.height = Math.ceil(cote * dpr);
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    const cx = cote / 2;
    const cy = cote / 2;
    const r = taille / 2;
    if (selectionne) {
        ctx.beginPath();
        ctx.arc(cx, cy, r + 4, 0, Math.PI * 2);
        ctx.fillStyle = MARQUEUR.halo;
        ctx.fill();
    }
    // Liseré blanc 2 px + ombre 0 1px 4px rgba(16,46,105,.3)
    ctx.save();
    ctx.shadowColor = 'rgba(16,46,105,.3)';
    ctx.shadowBlur = 4;
    ctx.shadowOffsetY = 1;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = '#FFFFFF';
    ctx.fill();
    ctx.restore();
    const interieur = r - 2;
    ctx.beginPath();
    ctx.arc(cx, cy, interieur, 0, Math.PI * 2);
    if (verdict === 'ni') {
        ctx.fillStyle = NI.corps;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(cx, cy, interieur - 0.5, 0, Math.PI * 2);
        ctx.lineWidth = 1;
        ctx.strokeStyle = NI.contour;
        ctx.stroke();
    } else {
        ctx.fillStyle = VERDICT_COULEURS[verdict].pastille;
        ctx.fill();
    }
    if (verdict === 'ni') { // « no data » : pas de glyphe
        s = {canvas, cote};
        sprites.set(cle, s);
        return s;
    }
    const icone = ICONES[verdict];
    const echelle = (selectionne ? 16 : 14) / 24;
    ctx.save();
    ctx.translate(cx - 12 * echelle, cy - 12 * echelle);
    ctx.scale(echelle, echelle);
    if (icone.mode === 'fill') {
        ctx.fillStyle = '#FFFFFF';
        ctx.fill(icone.trace);
    } else {
        ctx.lineWidth = icone.largeur;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = icone.couleur || '#FFFFFF';
        ctx.stroke(icone.trace);
    }
    ctx.restore();
    s = {canvas, cote};
    sprites.set(cle, s);
    return s;
}

// --- Style Abribacs (prototype) : sprite pré-rendu une fois par (verdict, taille, dpr), Path2D jamais tracé par point --
const CHEMINS_ABRIBAC = {
    corps: new Path2D(ABRIBAC.corps),
    glyphes: Object.fromEntries(Object.entries(GLYPHES).map(([k, g]) => [k, new Path2D(g.trace)])),
};
const spritesAbribac = new Map();

function toileHorsEcran(largeur, hauteur) {
    if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(largeur, hauteur);
    const c = document.createElement('canvas');
    c.width = largeur;
    c.height = hauteur;
    return c;
}

/**
 * Sprite d'abribac pour une taille (px, barème `tailleAbribac`) : le point géographique est au milieu du socle
 * (`ancreX`, `ancreY`), le centre visuel (hit-test) `decalageCentre` au-dessus, rayon de clic = `taille / 2`.
 * Ombre portée du handoff (0 1px 4px rgba(16,46,105,.3)) rendue ici, sous le liseré blanc, jamais par point.
 */
function spriteAbribac(verdict, taille, selectionne) {
    const dpr = window.devicePixelRatio || 1;
    const cle = `${verdict}|${taille}|${selectionne ? 1 : 0}|${dpr}`;
    let s = spritesAbribac.get(cle);
    if (s) return s;
    const marge = 8; // halo + ombre
    const cote = taille + marge * 2;
    const canvas = toileHorsEcran(Math.ceil(cote * dpr), Math.ceil(cote * dpr));
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    const k = taille / 24;
    const ni = verdict === 'ni';
    const couleur = VERDICT_COULEURS[verdict].pastille;
    if (selectionne) {
        ctx.beginPath();
        ctx.arc(cote / 2, marge + 12 * k, taille / 2 + 4, 0, Math.PI * 2);
        ctx.fillStyle = MARQUEUR.halo;
        ctx.fill();
    }
    ctx.translate(marge, marge);
    ctx.scale(k, k);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const encre = VERDICT_COULEURS[verdict].texte;
    // Corps : ombre + liseré blanc extérieur, puis fond + contour coloré (non inspecté : gris « no data », contour plein 1 px).
    // Liseré et contour sont en px d'écran (barème par taille) : le contexte est à l'échelle k, on divise.
    const contour = contourAbribac(taille) / k;
    const lisere = lisereAbribac(taille) / k;
    ctx.save();
    ctx.shadowColor = 'rgba(16,46,105,.3)';
    ctx.shadowBlur = 4 / k;
    ctx.shadowOffsetY = 1 / k;
    ctx.lineWidth = contour + 2 * lisere;
    ctx.strokeStyle = '#FFFFFF';
    ctx.fillStyle = '#FFFFFF';
    ctx.stroke(CHEMINS_ABRIBAC.corps);
    ctx.fill(CHEMINS_ABRIBAC.corps);
    ctx.restore();
    ctx.fillStyle = ni ? NI.corps : couleur;
    ctx.fill(CHEMINS_ABRIBAC.corps);
    ctx.lineWidth = ni ? 1 / k : contour; // « no data » : contour plein 1 px d'écran
    ctx.strokeStyle = ni ? NI.contour : encre;
    ctx.stroke(CHEMINS_ABRIBAC.corps);
    ctx.setLineDash([]);
    // Trappe (blanc, sans contour, absente du dessin retenu) et socle (blanc 60 %).
    const t = ABRIBAC.trappe;
    if (t) {
        ctx.fillStyle = '#FFFFFF';
        ctx.beginPath();
        ctx.roundRect ? ctx.roundRect(t.x, t.y, t.w, t.h, t.rx) : ctx.rect(t.x, t.y, t.w, t.h);
        ctx.fill();
    }
    const so = ABRIBAC.socle;
    ctx.fillStyle = 'rgba(255,255,255,.6)';
    ctx.beginPath();
    ctx.roundRect ? ctx.roundRect(so.x, so.y, so.w, so.h, so.rx) : ctx.rect(so.x, so.y, so.w, so.h);
    ctx.fill();
    // Glyphe (jamais la couleur seule) : absent pour « non inspecté ». Blanc dans le caisson, ou couleur du verdict
    // sur pastille blanche quand `ABRIBAC.pastille` est défini.
    if (!ni) {
        const p = ABRIBAC.pastille;
        if (p) {
            ctx.beginPath();
            ctx.arc(p.cx, p.cy, p.r, 0, Math.PI * 2);
            ctx.fillStyle = '#FFFFFF';
            ctx.fill();
        }
        const encreGlyphe = p ? couleur : ABRIBAC.glyphe.couleur;
        const g = GLYPHES[verdict];
        if (g.mode === 'fill') {
            ctx.save();
            ctx.translate(g.transform.tx, g.transform.ty);
            ctx.scale(g.transform.s, g.transform.s);
            ctx.fillStyle = encreGlyphe;
            ctx.fill(CHEMINS_ABRIBAC.glyphes[verdict]);
            ctx.restore();
        } else {
            ctx.lineWidth = LARGEUR_GLYPHE;
            ctx.strokeStyle = encreGlyphe;
            ctx.stroke(CHEMINS_ABRIBAC.glyphes[verdict]);
        }
    }
    // Ancrage : centre du socle ; le centre visuel (hit-test) est 9 unités au-dessus.
    s = {canvas, cote, ancreX: cote / 2, ancreY: marge + ABRIBAC.ancreY * k, decalageCentre: (ABRIBAC.ancreY - 12) * k, rayon: taille / 2};
    spritesAbribac.set(cle, s);
    return s;
}

// --- Heatmap (principe de PAT-04 : disques radiaux accumulés puis colorisés, sans dépendance) -----------------------
let degrade = null;
function degradeHeat() {
    if (degrade) return degrade;
    const c = document.createElement('canvas');
    c.width = 1;
    c.height = 256;
    const g = c.getContext('2d');
    const grad = g.createLinearGradient(0, 0, 0, 256);
    grad.addColorStop(0.0, 'rgba(255, 230, 120, 0)');
    grad.addColorStop(0.25, 'rgba(255, 210, 90, 0.55)');
    grad.addColorStop(0.55, 'rgba(243, 154, 63, 0.8)');
    grad.addColorStop(0.85, 'rgba(216, 60, 20, 0.9)');
    grad.addColorStop(1.0, 'rgba(160, 20, 20, 0.95)');
    g.fillStyle = grad;
    g.fillRect(0, 0, 1, 256);
    degrade = g.getImageData(0, 0, 1, 256).data;
    return degrade;
}

function dessinerHeat(ctx, points, poidsMax, origine, k, intensite = 1) {
    // Travail en pixels de canvas : k = pixels de canvas par pixel de calque (ratio rétine).
    const largeur = ctx.canvas.width;
    const hauteur = ctx.canvas.height;
    const off = document.createElement('canvas');
    off.width = largeur;
    off.height = hauteur;
    const o = off.getContext('2d');
    const r = (RAYON_HEAT + FLOU_HEAT) * k;
    for (const d of points) {
        const x = (d.x - origine.x) * k;
        const y = (d.y - origine.y) * k;
        if (x < -r || y < -r || x > largeur + r || y > hauteur + r) continue;
        const a = Math.min(1, 0.35 + 0.65 * (d.poids / poidsMax)) * intensite;
        const g = o.createRadialGradient(x, y, 0, x, y, r);
        g.addColorStop(0, `rgba(0,0,0,${a})`);
        g.addColorStop(RAYON_HEAT / (RAYON_HEAT + FLOU_HEAT), `rgba(0,0,0,${a * 0.45})`);
        g.addColorStop(1, 'rgba(0,0,0,0)');
        o.fillStyle = g;
        o.beginPath();
        o.arc(x, y, r, 0, Math.PI * 2);
        o.fill();
    }
    const img = o.getImageData(0, 0, largeur, hauteur);
    const px = img.data;
    const grad = degradeHeat();
    for (let i = 0; i < px.length; i += 4) {
        const a = px[i + 3];
        if (a === 0) continue;
        const j = a * 4;
        px[i] = grad[j];
        px[i + 1] = grad[j + 1];
        px[i + 2] = grad[j + 2];
        px[i + 3] = grad[j + 3];
    }
    o.putImageData(img, 0, 0);
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.drawImage(off, 0, 0);
    ctx.restore();
}

// Remplissage moyen (collecte) : moyenne locale pondérée, calculée sur une grille sous-échantillonnée (x2) puis agrandie.
const RAYON_REMPLISSAGE = 40; // px de calque : support du noyau gaussien
const SOUS_ECHANTILLONNAGE = 2;
const OPACITE_REMPLISSAGE = 0.55;

function dessinerRemplissage(ctx, points, origine, k, largeurCalque) {
    const largeur = ctx.canvas.width;
    const hauteur = ctx.canvas.height;
    const L = Math.ceil(largeur / SOUS_ECHANTILLONNAGE);
    const H = Math.ceil(hauteur / SOUS_ECHANTILLONNAGE);
    const f = k / SOUS_ECHANTILLONNAGE;
    const pts = points.map((p) => ({x: (p.x - origine.x) * f, y: (p.y - origine.y) * f, w: p.w}));
    const m = moyenneLocale(pts, {largeur: L, hauteur: H, rayon: RAYON_REMPLISSAGE * f});
    const img = new ImageData(L, H);
    const px = img.data;
    for (let i = 0; i < L * H; i++) {
        if (!(m.D[i] > m.seuil)) continue;
        const [r, g, b] = rgbRemplissage(m.N[i] / m.D[i]);
        px[4 * i] = r;
        px[4 * i + 1] = g;
        px[4 * i + 2] = b;
        px[4 * i + 3] = 255;
    }
    const off = document.createElement('canvas');
    off.width = L;
    off.height = H;
    off.getContext('2d').putImageData(img, 0, 0);
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = OPACITE_REMPLISSAGE;
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(off, 0, 0, largeur, hauteur);
    ctx.restore();
}

/** Légende « Remplissage moyen » (dégradé + bornes), dessinée en bas à gauche de la vue (coordonnées de calque). */
function dessinerLegendeRemplissage(ctx, map) {
    const w = 170;
    const h = 58;
    const coin = map.containerPointToLayerPoint([12, map.getSize().y - 116 - h]);
    ctx.save();
    ctx.fillStyle = 'rgba(255,255,255,.92)';
    ctx.strokeStyle = '#D5DAE1';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.rect(coin.x, coin.y, w, h);
    ctx.fill();
    ctx.stroke();
    const grad = ctx.createLinearGradient(coin.x + 10, 0, coin.x + w - 10, 0);
    const bas = PALIERS_REMPLISSAGE[0][0];
    const haut = PALIERS_REMPLISSAGE[PALIERS_REMPLISSAGE.length - 1][0];
    for (const [t, [r, g, b]] of PALIERS_REMPLISSAGE) grad.addColorStop((t - bas) / (haut - bas), `rgb(${r},${g},${b})`);
    ctx.fillStyle = '#2D333B';
    ctx.font = '700 11px "Open Sans", sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText('Remplissage moyen', coin.x + 10, coin.y + 16);
    ctx.fillStyle = grad;
    ctx.fillRect(coin.x + 10, coin.y + 22, w - 20, 8);
    ctx.fillStyle = '#6B7480';
    ctx.font = '400 9px "Open Sans", sans-serif';
    PALIERS_REMPLISSAGE.forEach(([t], i) => {
        const x = coin.x + 10 + ((t - bas) / (haut - bas)) * (w - 20);
        ctx.textAlign = i === 0 ? 'left' : i === PALIERS_REMPLISSAGE.length - 1 ? 'right' : 'center';
        ctx.fillText(`${Math.round(t * 100)} %`, x, coin.y + 42);
        if (t === CIBLE_REMPLISSAGE) ctx.fillText('(cible)', x, coin.y + 52);
    });
    ctx.restore();
}

const ORDRE_DESSIN = ['ni', 'ras', 'pb', 'imp']; // les problèmes au-dessus des RAS, eux-mêmes au-dessus du gris

const CouchePav = L.Layer.extend({
    initialize() {
        this._pavs = [];
        this._etats = null;
        this._style = 'points';
        this._acte = null;
        this._selectedId = null;
        this._dessines = []; // éléments cliquables du dernier dessin : {x, y, id} | {x, y, cluster}
        this.derniereDureeMs = 0;
    },

    configurer({pavs, etats, style, acte, selectedId}) {
        this._pavs = pavs;
        this._acte = acte;
        this._etats = etats;
        this._style = style;
        this._selectedId = selectedId;
        this._redraw();
    },

    onAdd(map) {
        this._map = map;
        this._renderer = L.canvas({padding: 0.5});
        this._renderer.addTo(map);
        this._renderer.on('update', this._redraw, this); // le canvas Leaflet est effacé à chaque moveend / zoomend
        this._redraw();
    },

    onRemove(map) {
        if (this._renderer) {
            this._renderer.off('update', this._redraw, this);
            map.removeLayer(this._renderer);
            this._renderer = null;
        }
        this._map = null;
    },

    _redraw() {
        const map = this._map;
        if (!map || !this._renderer || !this._renderer._ctx || !this._etats) return;
        const t0 = performance.now();
        const ctx = this._renderer._ctx;
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
        ctx.restore();
        this._dessines = [];
        const etats = this._etats;
        const origine = this._renderer._bounds.min;

        // Projection unique (lat/lng -> point de calque) ; les PAV sans coordonnées ne sont pas dessinés.
        const proj = [];
        for (const pav of this._pavs) {
            if (pav.lat == null || pav.lng == null) continue;
            const pt = map.latLngToLayerPoint([pav.lat, pav.lng]);
            proj.push({id: pav.id, x: pt.x, y: pt.y, etat: etats.get(pav.id)});
        }

        if (this._style === 'grille') this._dessinerGrille(ctx);
        else if (this._style === 'aplat') { /* l'aplat est une couche vectorielle (CarteCommunesLayer) */ }
        else if (this._style === 'heatmap') this._dessinerHeat(ctx, proj, origine, poidsDefaut);
        else if (this._style === 'tonnage') this._dessinerRemplissage(ctx, proj, origine);
        else if (this._style === 'abribacs') this._dessinerAbribacs(ctx, proj);
        else if (this._style === 'clusters') this._dessinerClusters(ctx, proj, map);
        else this._dessinerPoints(ctx, proj);

        this.derniereDureeMs = performance.now() - t0;
    },

    _dessinerPoints(ctx, proj) {
        const parVerdict = {ni: [], ras: [], pb: [], imp: []};
        for (const p of proj) parVerdict[p.etat.verdict].push(p);
        for (const v of ORDRE_DESSIN) this._poser(ctx, parVerdict[v], v);
        const sel = this._selectedId == null ? null : proj.find((p) => p.id === this._selectedId);
        if (sel) this._poser(ctx, [sel], sel.etat.verdict, true);
    },

    _poser(ctx, liste, verdict, selectionne = false) {
        const s = sprite(verdict, selectionne);
        const demi = s.cote / 2;
        const {_bounds: b} = this._renderer;
        const marge = s.cote;
        for (const p of liste) {
            this._dessines.push({x: p.x, y: p.y, id: p.id, selectionne});
            if (p.x < b.min.x - marge || p.y < b.min.y - marge || p.x > b.max.x + marge || p.y > b.max.y + marge) continue;
            ctx.drawImage(s.canvas, p.x - demi, p.y - demi, s.cote, s.cote);
        }
    },

    _dessinerAbribacs(ctx, proj) {
        const parVerdict = {ni: [], ras: [], pb: [], imp: []};
        for (const p of proj) parVerdict[p.etat.verdict].push(p);
        const zoom = this._map.getZoom();
        for (const v of ORDRE_DESSIN) this._poserAbribacs(ctx, parVerdict[v], v, zoom);
        const sel = this._selectedId == null ? null : proj.find((p) => p.id === this._selectedId);
        if (sel) this._poserAbribacs(ctx, [sel], sel.etat.verdict, zoom, true);
    },

    _poserAbribacs(ctx, liste, verdict, zoom, selectionne = false) {
        const s = spriteAbribac(verdict, tailleAbribac(zoom, selectionne), selectionne);
        const {_bounds: b} = this._renderer;
        const marge = s.cote;
        for (const p of liste) {
            // Élément cliquable centré sur le corps de l'abribac, pas sur son socle.
            this._dessines.push({x: p.x, y: p.y - s.decalageCentre, id: p.id, selectionne, rayon: s.rayon});
            if (p.x < b.min.x - marge || p.y < b.min.y - marge || p.x > b.max.x + marge || p.y > b.max.y + marge) continue;
            ctx.drawImage(s.canvas, p.x - s.ancreX, p.y - s.ancreY, s.cote, s.cote);
        }
    },

    _dessinerClusters(ctx, proj, map) {
        const zoom = map.getZoom();
        const pointsParId = new Map(proj.map((p) => [p.id, p]));
        const pavsLocalises = this._pavs.filter((p) => p.lat != null && p.lng != null);
        const groupes = clusteriser(pavsLocalises, zoom, (p, z) => map.project([p.lat, p.lng], z));
        const parVerdict = {ni: [], ras: [], pb: [], imp: []};
        for (const g of groupes) {
            if (g.nb === 1) {
                const p = pointsParId.get(g.ids[0]);
                parVerdict[p.etat.verdict].push(p);
            } else {
                const pt = map.latLngToLayerPoint([g.lat, g.lng]);
                this._clusterDessine(ctx, pt, g);
            }
        }
        for (const v of ORDRE_DESSIN) this._poser(ctx, parVerdict[v], v);
        // Le PAV sélectionné est toujours dessiné, au-dessus de son éventuel cluster.
        const sel = this._selectedId == null ? null : pointsParId.get(this._selectedId);
        if (sel) this._poser(ctx, [sel], sel.etat.verdict, true);
    },

    _clusterDessine(ctx, pt, g) {
        const rayon = Math.min(30, 16 + Math.log2(g.nb) * 2.4);
        this._dessines.push({x: pt.x, y: pt.y, cluster: g, rayon});
        ctx.save();
        ctx.shadowColor = 'rgba(16,46,105,.25)';
        ctx.shadowBlur = 4;
        ctx.shadowOffsetY = 1;
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, rayon, 0, Math.PI * 2);
        ctx.fillStyle = MARQUEUR.cluster.fond;
        ctx.fill();
        ctx.restore();
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, rayon - 1.5, 0, Math.PI * 2);
        ctx.lineWidth = 3;
        ctx.strokeStyle = NAVY;
        ctx.stroke();
        ctx.fillStyle = '#2D333B';
        ctx.font = `${MARQUEUR.cluster.poids} ${g.nb >= 100 ? 12 : 13}px "Open Sans", sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(String(g.nb), pt.x, pt.y + 0.5);
    },

    _dessinerGrille(ctx) {
        const map = this._map;
        const cellules = cellulesGrille(this._pavs, this._etats, undefined, this._acte);
        ctx.save();
        for (const c of cellules) {
            const a = map.latLngToLayerPoint([c.nord, c.ouest]);
            const b = map.latLngToLayerPoint([c.sud, c.est]);
            // Cellule sans donnée (aucun PAV inspecté, ou aucune collecte) : gris.
            ctx.fillStyle = c.part == null ? '#C9CFD6' : this._acte === 'collecte' ? couleurRemplissage(c.part) : couleurPart(c.part);
            ctx.globalAlpha = 0.72;
            ctx.fillRect(a.x, a.y, b.x - a.x, b.y - a.y);
            ctx.globalAlpha = 1;
            ctx.lineWidth = 1;
            ctx.strokeStyle = 'rgba(255,255,255,.9)';
            ctx.strokeRect(a.x, a.y, b.x - a.x, b.y - a.y);
        }
        ctx.restore();
        this._cellules = cellules;
    },

    _dessinerHeat(ctx, proj, origine, poidsDe, intensite = 1) {
        const ponderes = [];
        let max = 1;
        for (const p of proj) {
            const poids = poidsDe(p.etat);
            if (poids > 0) {
                ponderes.push({x: p.x, y: p.y, poids});
                if (poids > max) max = poids;
            }
        }
        if (ponderes.length > 0) dessinerHeat(ctx, ponderes, max, origine, ctx.canvas.width / this._renderer._bounds.getSize().x, intensite);
    },

    _dessinerRemplissage(ctx, proj, origine) {
        const points = [];
        for (const p of proj) {
            const w = poidsRemplissage(p.etat);
            if (w != null) points.push({x: p.x, y: p.y, w});
        }
        const k = ctx.canvas.width / this._renderer._bounds.getSize().x;
        if (points.length > 0) dessinerRemplissage(ctx, points, origine, k);
        dessinerLegendeRemplissage(ctx, this._map);
    },

    /** Élément cliquable le plus proche d'un point de calque (marqueur ou cluster), sinon null. */
    trouver(layerPoint) {
        let meilleur = null;
        let dmin = Infinity;
        for (const d of this._dessines) {
            const dist = Math.hypot(d.x - layerPoint.x, d.y - layerPoint.y);
            const seuil = d.rayon != null ? d.rayon // cluster ou abribac : rayon porté par l'élément dessiné
                : (d.selectionne ? RAYON_CLIC_SELECTION : RAYON_CLIC);
            if (dist <= seuil && dist < dmin) {
                dmin = dist;
                meilleur = d;
            }
        }
        return meilleur;
    },
});

/** Wrapper React : une seule instance de couche, synchronisée avec les props. */
export default function CartePavCanvasLayer({pavs, etats, style, acte, selectedId, onSelect, onMesure, onVide}) {
    const map = useMap();
    const couche = useRef(null);
    const rappel = useRef(onSelect);
    rappel.current = onSelect;
    const rappelVide = useRef(onVide);
    rappelVide.current = onVide;
    const mesure = useRef(onMesure);
    mesure.current = onMesure;

    useEffect(() => {
        const layer = new CouchePav();
        couche.current = layer;
        layer.addTo(map);
        const surClic = (e) => {
            const d = layer.trouver(e.layerPoint);
            if (d && d.cluster) {
                map.setView([d.cluster.lat, d.cluster.lng], Math.min(map.getZoom() + 2, map.getMaxZoom() || 19));
            } else {
                rappel.current(d ? d.id : null);
                // Aucun PAV touché : le clic peut viser un circuit (calque circuits).
                if (!d && rappelVide.current) rappelVide.current(e.latlng, !!(e.originalEvent && e.originalEvent.shiftKey));
            }
        };
        map.on('click', surClic);
        const surSouris = (e) => {
            const d = layer.trouver(e.layerPoint);
            map.getContainer().style.cursor = d ? 'pointer' : '';
        };
        map.on('mousemove', surSouris);
        return () => {
            map.off('click', surClic);
            map.off('mousemove', surSouris);
            map.getContainer().style.cursor = '';
            map.removeLayer(layer);
            couche.current = null;
        };
    }, [map]);

    useEffect(() => {
        if (!couche.current) return;
        couche.current.configurer({pavs, etats, style, acte, selectedId});
        if (mesure.current) mesure.current(couche.current.derniereDureeMs);
    }, [pavs, etats, style, acte, selectedId]);

    return null;
}
